/**
 * Client for POST /api/cognitory/v2/provisioning-setup/library-state (read-only).
 */

import {
  COGNITORY_V2_PROVISIONING_LIBRARY_STATE_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export const PROVISIONING_LIBRARY_STATE_SETUP_PHASES = [
  "bootstrap_required",
  "library_initialized_draft",
  "library_processing",
  "library_activation_ready",
] as const;

export type ProvisioningLibraryStateSetupPhase =
  (typeof PROVISIONING_LIBRARY_STATE_SETUP_PHASES)[number];

export type ProvisioningLibraryStateClientResult =
  | {
      kind: "resolved";
      requestId: string;
      setupPhase: ProvisioningLibraryStateSetupPhase;
    }
  | { kind: "library_state_conflict"; requestId: string }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "system_not_eligible"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

function hasExactKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const keys = Object.keys(record);
  if (keys.length !== allowed.length) {
    return false;
  }
  for (const key of allowed) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      return false;
    }
  }
  return true;
}

export function parseNonemptyRequestId(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isSetupPhase(
  value: unknown,
): value is ProvisioningLibraryStateSetupPhase {
  return (
    typeof value === "string" &&
    (PROVISIONING_LIBRARY_STATE_SETUP_PHASES as readonly string[]).includes(
      value,
    )
  );
}

async function postProvisioningLibraryState(
  cognitoryId: string,
  input?: {
    apiBaseUrl?: string;
    bearerToken?: string | null;
    fetchImpl?: typeof fetch;
  },
): Promise<{ status: number; body: unknown }> {
  const fetchImpl = input?.fetchImpl ?? fetch;
  const token =
    input?.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { status: 401, body: null };
  }

  const base = input?.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  try {
    const response = await fetchImpl(
      `${base}${COGNITORY_V2_PROVISIONING_LIBRARY_STATE_PATH}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        cache: "no-store",
        body: JSON.stringify({ cognitoryId }),
      },
    );
    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
    return { status: response.status, body: parsed };
  } catch {
    return { status: 503, body: null };
  }
}

type LibraryStateDenialKind =
  | "unauthenticated"
  | "forbidden"
  | "invalid_request"
  | "system_not_eligible"
  | "temporary_failure";

function parseDenial(
  status: number,
  body: unknown,
  expectedDecision: LibraryStateDenialKind,
  expectedStatus: number,
): { kind: LibraryStateDenialKind; requestId: string } | null {
  if (status !== expectedStatus || !body || typeof body !== "object") {
    return null;
  }
  const record = body as Record<string, unknown>;
  if (!hasExactKeys(record, ["decision", "requestId"])) {
    return null;
  }
  if (record.decision !== expectedDecision) {
    return null;
  }
  const requestId = parseNonemptyRequestId(record.requestId);
  if (!requestId) {
    return null;
  }
  return { kind: expectedDecision, requestId };
}

export async function fetchProvisioningLibraryState(input: {
  cognitoryId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<ProvisioningLibraryStateClientResult> {
  const { status, body } = await postProvisioningLibraryState(
    input.cognitoryId,
    input,
  );

  if (status === 401) {
    const denial = parseDenial(status, body, "unauthenticated", 401);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 403) {
    const denial = parseDenial(status, body, "forbidden", 403);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 400) {
    const invalid = parseDenial(status, body, "invalid_request", 400);
    if (invalid) {
      return invalid;
    }
    const ineligible = parseDenial(status, body, "system_not_eligible", 400);
    if (ineligible) {
      return ineligible;
    }
    return { kind: "unable_to_verify", requestId: null };
  }
  if (status === 503) {
    const denial = parseDenial(status, body, "temporary_failure", 503);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 409) {
    if (!body || typeof body !== "object") {
      return { kind: "unable_to_verify", requestId: null };
    }
    const record = body as Record<string, unknown>;
    if (
      !hasExactKeys(record, ["decision", "requestId", "mode"]) ||
      record.decision !== "provisioning_library_state_conflict" ||
      record.mode !== "read_only"
    ) {
      return { kind: "unable_to_verify", requestId: null };
    }
    const requestId = parseNonemptyRequestId(record.requestId);
    if (!requestId) {
      return { kind: "unable_to_verify", requestId: null };
    }
    return { kind: "library_state_conflict", requestId };
  }
  if (status === 200) {
    if (!body || typeof body !== "object") {
      return { kind: "unable_to_verify", requestId: null };
    }
    const record = body as Record<string, unknown>;
    if (
      !hasExactKeys(record, ["decision", "requestId", "mode", "setupPhase"]) ||
      record.decision !== "provisioning_library_state_resolved" ||
      record.mode !== "read_only" ||
      !isSetupPhase(record.setupPhase)
    ) {
      return { kind: "unable_to_verify", requestId: null };
    }
    const requestId = parseNonemptyRequestId(record.requestId);
    if (!requestId) {
      return { kind: "unable_to_verify", requestId: null };
    }
    return {
      kind: "resolved",
      requestId,
      setupPhase: record.setupPhase,
    };
  }

  return { kind: "unable_to_verify", requestId: null };
}
